import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'react-feather';
import { Button, EmptyState, Loader } from '../../../ui';
import { PriorityBadge, TaskStatusBadge } from './ui-pending';
import { formatTaskId, Task, tasksApi } from '../../../Tasks/tasksApi';
import './LearnerTaskList.scss';

/**
 * Every ticket raised for one learner, newest first.
 *
 * Shared on purpose. It started inside the learner drawer, and the student
 * profile needs the same answer to the same question — "what has been raised
 * for this person?" — so the two render the same component rather than
 * drifting into two lists that look alike and behave differently.
 *
 * `onCount` is for a caller that shows the number in its own heading, e.g.
 * the drawer's "Tasks (2)". It fires once the list has loaded.
 */
export const LearnerTaskList: React.FC<{
  studentExternalId: string;
  onCount?: (n: number) => void;
}> = ({ studentExternalId, onCount }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<Task[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTasks(null);
    tasksApi<{ tasks: Task[]; total: number }>(
      `/v1/tasks?customerExternalId=${encodeURIComponent(studentExternalId)}&take=50`,
    )
      .then((r) => {
        if (cancelled) return;
        setTasks(r.tasks);
        onCount?.(r.tasks.length);
      })
      .catch(() => {
        if (cancelled) return;
        setTasks([]);
        onCount?.(0);
      });
    return () => {
      cancelled = true;
    };
    // onCount is a render-scoped callback in most callers; depending on it
    // would refetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentExternalId]);

  if (tasks === null) return <Loader label="Loading tasks…" />;
  if (!tasks.length) {
    return <EmptyState title="No tasks" description="Nothing has been raised for this student yet." />;
  }

  return (
    <div className="ml-tasks">
      {/* Two ways through to the ticket, because they cost different things.
          Opening here is one click but coming back re-mounts the page and
          reloads the student, losing whatever got them here; opening in a tab
          leaves this exactly as it is. */}
      {tasks.map((t) => {
        const href = `${process.env.PUBLIC_URL}/pages/rm-tasks/${t.id}`;
        const ref = formatTaskId(t.taskNumber);
        return (
          <div key={t.id} className="ml-task">
            <div className="ml-task__head">
              <span className="rm-id-chip ml-task__ref">{ref}</span>
              <span className="ml-task__type ds-strong">{t.taskType.name}</span>
              <div className="ml-task__actions">
                <Button
                  size="sm"
                  title={`Open ${ref} here`}
                  aria-label={`Open ${ref} here`}
                  onClick={() => navigate(href)}
                >
                  Open
                </Button>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open ${ref} in a new tab — keeps this view open`}
                  aria-label={`Open ${ref} in a new tab`}
                  className="ds-btn ds-btn--secondary ds-btn--sm"
                >
                  <ExternalLink size={14} aria-hidden />
                </a>
              </div>
            </div>
            <div className="rm-row">
              <TaskStatusBadge status={t.status} />
              <PriorityBadge priority={t.priority} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default LearnerTaskList;
